import React from 'react';
import { Typography, Avatar } from 'antd';
import { MailOutlined, CheckOutlined } from '@ant-design/icons';

import { toSiteDisplay, sortSitesForPreview } from '../../utils/SiteDisplayHelper';
import { ccyIsoCodeToSymbolMap } from '../../utils/CurrencyUtils';

import { useTeamMembers } from './useTeamMembers';

const { Text } = Typography;

function formatMemberName(displayName) {
  const parts = String(displayName || 'Member').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'Member';
  const firstName = parts[0];
  const lastInitial = parts.length > 1 ? `${parts[parts.length - 1][0]}.` : '';
  return [firstName, lastInitial].filter(Boolean).join(' ');
}

const EmailSiteRow = ({ site }) => {
  const display = site && (site.headline || site.buttonText) ? site : toSiteDisplay(site);
  if (!display) return null;
  const {
    headline, buttonText, displayName, imageUrl, color, textColor,
  } = display;

  return (
    <div style={{
      background: '#141414',
      borderRadius: '10px',
      color: '#fff',
      padding: '10px 12px',
      marginBottom: '10px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    }}
    >
      {imageUrl && (
        <img
          src={imageUrl}
          alt={displayName || 'site'}
          style={{
            width: 28,
            height: 28,
            borderRadius: '6px',
            objectFit: 'cover',
            flexShrink: 0,
          }}
        />
      )}
      <div style={{ flex: 1, textAlign: 'left' }}>
        <div style={{ fontSize: '15px', fontWeight: 600, lineHeight: 1.2, textAlign: 'left' }}>
          {headline || 'Useful link'}
        </div>
        <a
          href={display.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-block',
            marginTop: '2px',
            fontSize: '12px',
            color: '#cfe4ff',
            textDecoration: 'none',
            fontWeight: 500,
          }}
        >
          {buttonText}
          {' '}
          -&gt;
        </a>
        {displayName && (
          <div style={{ marginTop: '2px', fontSize: '12px', opacity: 0.8, textAlign: 'left' }}>
            {displayName}
          </div>
        )}
      </div>
    </div>
  );
};

const ThankYouEmailPreview = ({ sites, team }) => {
  const sortedSites = sortSitesForPreview(sites || []);
  const { teamName, teamId, defaultCurrency, currency } = team || {};
  const teamMembers = useTeamMembers(teamId);
  const visibleMembers = (teamMembers || []).filter((member) => !member?.isDeleted).slice(0, 12);
  const useCurrency = defaultCurrency || currency || 'USD';
  const currencySymbol = ccyIsoCodeToSymbolMap[useCurrency] || '$';

  return (
    <div style={{
      position: 'sticky',
      top: '24px',
      width: '100%',
      maxWidth: '420px',
      margin: '0 auto',
    }}
    >
      <div style={{
        textAlign: 'center',
        marginBottom: '12px',
        color: '#8c8c8c',
        fontSize: '12px',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        fontWeight: 'bold',
      }}
      >
        <MailOutlined />
        {' '}
        Thank You Email Preview
      </div>

      <div style={{
        background: '#f5f5f5',
        borderRadius: '12px',
        border: '1px solid #e5e5e5',
        overflow: 'hidden',
      }}
      >
        <div style={{ background: '#2cad5a', color: '#fff', padding: '16px', textAlign: 'center' }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            background: '#fff',
            color: '#2cad5a',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '10px',
          }}
          >
            <CheckOutlined style={{ fontSize: '26px' }} />
          </div>
          <div style={{ fontSize: '20px', fontWeight: 700 }}>Your tip was successful!</div>
          {teamName && <div style={{ marginTop: '4px', fontSize: '13px', opacity: 0.9 }}>{teamName}</div>}
        </div>

        <div style={{ padding: '16px' }}>
          <div style={{ textAlign: 'center', marginBottom: '14px' }}>
            <div style={{ fontSize: '28px', lineHeight: 1 }}>Hi there,</div>
            <div style={{ marginTop: '6px', fontSize: '14px' }}>Powerful links</div>
          </div>

          {sortedSites.length > 0 ? (
            sortedSites.map((site) => (
              <EmailSiteRow
                key={`${site.siteId || site.id || 'site'}-${site.type || 'type'}-${site.brand || 'brand'}`}
                site={site}
              />
            ))
          ) : (
            <div style={{ textAlign: 'center', color: '#999', padding: '12px 0' }}>
              <Text type="secondary">Activate links to preview the thank you email.</Text>
            </div>
          )}

          <div style={{
            marginTop: '10px',
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'center',
            gap: '16px',
            fontSize: '13px',
            fontWeight: 600,
          }}
          >
            <a href="#tip-again" style={{ color: '#1f5fae' }}>Tip again</a>
            <a href="#view-on-web" style={{ color: '#1f5fae' }}>View on web</a>
          </div>

          <div style={{
            margin: '0 auto 16px',
            maxWidth: '260px',
            border: '1px dashed #9ca3af',
            borderRadius: '10px',
            padding: '14px 14px 10px',
            background: '#fff',
          }}
          >
            <div style={{ textAlign: 'center', fontSize: '22px', fontWeight: 700, marginBottom: '8px' }}>Receipt</div>
            <div style={{ textAlign: 'center', marginBottom: '10px', color: '#555' }}>
              {teamName || 'Your Team'}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
              <span>Tip amount</span>
              <span>{`${currencySymbol}10.00`}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
              <span>Tip fee</span>
              <span>{`${currencySymbol}0.65`}</span>
            </div>
            <div style={{ borderTop: '1px solid #444', margin: '6px 0 6px' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: 700 }}>
              <span>Total charged</span>
              <span>{`${currencySymbol}10.65`}</span>
            </div>
          </div>

          <div style={{ marginTop: '18px', textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 700, marginBottom: '10px' }}>Meet the Team</div>
            {visibleMembers.length > 0 ? (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '10px',
              }}
              >
                {visibleMembers.map((member) => {
                  const initials = (member.displayName || 'M')
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .slice(0, 2);
                  return (
                    <div key={member.clientId || member.displayName} style={{ width: '54px' }}>
                      <Avatar
                        src={member.avatar}
                        size={38}
                        style={{ backgroundColor: '#1f5fae', marginBottom: '4px' }}
                      >
                        {!member.avatar && initials}
                      </Avatar>
                      <div style={{ fontSize: '11px', lineHeight: 1.2, color: '#333' }}>
                        {formatMemberName(member.displayName)}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Team members will appear here.
              </Text>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThankYouEmailPreview;
